import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useSortable } from '@/hooks/useSortable'

interface Runner {
  id: number
  name: string
  slug: string
  status: string
  status_color: string
  assignment_count: number
  recent_error_event_count: number
  last_heartbeat_at: string | null
  has_public_key: boolean
  created_at: string
}

interface Props {
  runners: Runner[]
}

function statusChipColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'approved': return 'success'
    case 'pending': return 'warning'
    case 'denied': return 'error'
    case 'revoked': return 'default'
    default: return 'default'
  }
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'last_heartbeat_at', label: 'Last Heartbeat' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ runners }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(runners, 'name')
  const [deleteTarget, setDeleteTarget] = useState<Runner | null>(null)

  function handleDelete() {
    if (deleteTarget) {
      router.delete(`/manage/runners/${deleteTarget.slug}`)
      setDeleteTarget(null)
    }
  }

  return (
    <SidebarLayout
      section="manage"
      title="Runners"
      action={
        <Button component={Link} href="/manage/runners/new" variant="contained">
          New runner
        </Button>
      }
    >
      {runners.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={orderBy}
              label="Sort by"
              onChange={e => handleSort(e.target.value)}
            >
              {SORT_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => handleSort(orderBy)}>
            {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No runners found. Create one to get started.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((runner) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={runner.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1, mr: 1 }}>
                    {runner.name}
                  </Typography>
                  <Chip
                    label={runner.status}
                    size="small"
                    color={statusChipColor(runner.status)}
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip label={`${runner.assignment_count} agents`} size="small" variant="outlined" />
                  {runner.recent_error_event_count > 0 ? (
                    <Chip label={`${runner.recent_error_event_count} errors (24h)`} size="small" color="error" variant="outlined" />
                  ) : (
                    <Chip label="0 errors (24h)" size="small" variant="outlined" />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Heartbeat: {formatTimeAgo(runner.last_heartbeat_at)}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                  Created {new Date(runner.created_at).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button component={Link} href={`/manage/runners/${runner.slug}/edit`} size="small">
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setDeleteTarget(runner)}>
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete runner"
        message={`Are you sure you want to delete runner '${deleteTarget?.name}'? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </SidebarLayout>
  )
}
